import React, { FC, useEffect, useRef, useState } from 'react';
import { t, useBehaviorSubject, useSubject } from '../../../../utils/helpers';
import { Loader } from '@ui-kit/components/loader';
import search from '../../../../assets/images/search.svg';
import { LastInOutPlayer } from '../../../lastInOutPlayer/lastInOutPlayer';
import { container } from 'tsyringe';
import { OnlinePlayersList } from './OnlinePlayersList/OnlinePlayersList';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { IAuthService } from '@base/features/auth/model/interfaces';

interface PropsType {
  selectedPlayers?: number[];
  onChange?: (pid: number) => void;
}

const SORTITEMS = {
  PLAYER: 'player',
  RATING: 'rating',
  STATUS: 'status',
};

const sortCallbacks = {
  [SORTITEMS.PLAYER]: (p1: IPlayer, p2: IPlayer): number => {
    const playerName1 = p1.name.toLowerCase();
    const PlayerName2 = p2.name.toLowerCase();
    return playerName1 < PlayerName2 ? -1 : playerName1 === PlayerName2 ? 0 : 1;
  },

  [SORTITEMS.RATING]: (p1: IPlayer, p2: IPlayer): number => {
    const rating1 = p1.rating;
    const rating2 = p2.rating;
    return rating1 - rating2;
  },

  [SORTITEMS.STATUS]: (p1: IPlayer, p2: IPlayer): number => {
    const point1 = p1.getStatus();
    const point2 = p2.getStatus();
    return point1 < point2 ? -1 : point1 === point2 ? 0 : 1;
  },
};

function sortPlayers(sortOptions: { column: string; dir: string }, playersArray: IPlayer[]): IPlayer[] {
  if (playersArray.length < 2) return playersArray;
  const { column, dir } = sortOptions;

  playersArray.sort((p1: IPlayer, p2: IPlayer): number => {
    if (dir === 'desc') {
      [p1, p2] = [p2, p1];
    }
    return sortCallbacks[column](p1, p2);
  });
  return playersArray;
}

export const OnlinePlayersLayout: FC<PropsType> = ({ selectedPlayers, onChange }: PropsType) => {
  const authService: IAuthService = container.resolve('authService');
  const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');
  const [pl, setPlayers] = useState<IPlayer[]>();
  const currentUser = useSubject(authService.userSession);
  const lastInOutPlayer = useSubject(playersOnlineRepository.onInOutPlayer);
  const loginReady = useBehaviorSubject(playersOnlineRepository.onLoginReady);
  const [filter, setFilter] = useState('');
  const [sortOptions, setSortOptions] = useState({
    column: SORTITEMS.PLAYER,
    dir: 'asc',
  });

  useEffect(() => {
    const players = playersOnlineRepository.getPlayersOnline();
    setPlayers([...players.values()]);
    if (filter && players) {
      setPlayers([...players.values()].filter((p) => p.name.toLowerCase().includes(filter.toLowerCase())));
    }
  }, [lastInOutPlayer, playersOnlineRepository, filter]);

  function handlerSortTables(column: string) {
    let dir = sortOptions.dir;
    if (column === sortOptions.column) {
      dir = dir === 'asc' ? 'desc' : 'asc';
    }
    setSortOptions({
      column: column,
      dir: dir,
    });
  }

  if (!pl) return null;

  const sortedPlayers = sortPlayers(sortOptions, pl);

  const sortType = sortOptions.column;
  const sortDirection = sortOptions.dir;

  return (
    <div className="online-players">
      <div className="online-players__header">
        <PlayersHeader
          setFilter={setFilter}
          sortType={sortType}
          sortDirection={sortDirection}
          handlerSortTables={handlerSortTables}
          playersOnline={pl.length}
        />
        <div
          className={
            sortType === SORTITEMS.RATING
              ? `online-players__header-rating online-players__header-item ${sortDirection}`
              : `online-players__header-rating online-players__header-item`
          }
          onClick={() => handlerSortTables(SORTITEMS.RATING)}
        >
          {t('rating')}
        </div>
        <div
          className={
            sortType === SORTITEMS.STATUS
              ? `online-players__header-status online-players__header-item ${sortDirection}`
              : `online-players__header-status online-players__header-item`
          }
          onClick={() => handlerSortTables(SORTITEMS.STATUS)}
        >
          {t('status')}
        </div>
      </div>
      {loginReady && pl.length && currentUser ? (
        <OnlinePlayersList
          players={sortedPlayers}
          currentUser={currentUser.usr}
          selectedPlayers={selectedPlayers}
          onChange={onChange}
        />
      ) : (
        <Loader width={75} height={75} />
      )}
    </div>
  );
};

function PlayersHeader(props: {
  setFilter: (filter: string) => void;
  sortType: string;
  sortDirection: string;
  handlerSortTables: (c: string) => void;
  playersOnline: number;
}) {
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSearch) inputRef.current?.focus();
  }, [showSearch]);

  const toggleSearch = () => {
    setShowSearch(!showSearch);
    props.setFilter('');
  };

  return (
    <div className="online-players__header-player">
      <img src={search} alt="search" className="online-players__header__search-btn" onClick={toggleSearch} />
      {showSearch ? (
        <div className="online-players__header__search-input">
          <input type="text" onInput={(e) => props.setFilter(e.currentTarget.value)} ref={inputRef} />
          <div onClick={toggleSearch}>&times;</div>
        </div>
      ) : (
        <>
          <div
            className={
              props.sortType === SORTITEMS.PLAYER
                ? `online-players__header-item ${props.sortDirection}`
                : `online-players__header-item`
            }
          >
            <div className="online-players__header-title-wrapper">
              <h3
                className="online-players__header-title"
                onClick={() => props.handlerSortTables(SORTITEMS.PLAYER)}
              >
                {`${t('online')} ${props.playersOnline}`}
              </h3>
            </div>
          </div>
          <div className="online-players__header-in-out-player">
            <LastInOutPlayer />
          </div>
        </>
      )}
    </div>
  );
}
